import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Shield, Users, FileCheck, Sparkles, Lock, TrendingUp } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { siteConfig } from '../config/site';
import PropertyForm from '../components/PropertyForm';

const Home = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const scrollToForm = () => {
    document.getElementById('property-form')?.scrollIntoView({ behavior: 'smooth' });
  };

  const features = [
    {
      icon: FileCheck,
      title: 'Inserisci il tuo immobile gratuitamente',
      description: 'Nessun costo iniziale, solo immobili selezionati'
    },
    {
      icon: Shield,
      title: 'Reflow filtra e verifica le candidature',
      description: 'Sistema di screening avanzato per inquilini affidabili'
    },
    {
      icon: Users,
      title: 'Ti mettiamo in contatto solo con profili selezionati',
      description: 'Zero tempo sprecato con candidati non qualificati'
    }
  ];

  const differentiators = [
    {
      icon: Lock,
      text: 'Focus su sicurezza e selezione'
    },
    {
      icon: Users,
      text: 'Supporto nella gestione del rapporto'
    },
    {
      icon: TrendingUp,
      text: 'Vision futura: pagamenti integrati e scudo legale automatico'
    },
    {
      icon: Sparkles,
      text: 'Approccio tech e zero burocrazia inutile'
    }
  ];

  const progress = (siteConfig.foundingProperties.current / siteConfig.foundingProperties.total) * 100;

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0 z-0">
          <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 opacity-95 z-10"></div>
          <img 
            src="https://images.unsplash.com/photo-1624204386084-dd8c05e32226"
            alt="Modern Building"
            className="w-full h-full object-cover"
          />
        </div>

        {/* Animated gradient orbs */}
        <div className="absolute top-20 left-20 w-96 h-96 bg-blue-500 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob"></div>
        <div className="absolute top-40 right-20 w-96 h-96 bg-emerald-500 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob animation-delay-2000"></div>
        
        <div className={`relative z-20 max-w-6xl mx-auto px-6 py-20 text-center transition-all duration-1000 transform ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-500/10 border border-emerald-500/20 rounded-full mb-8 backdrop-blur-sm">
            <Sparkles className="w-4 h-4 text-emerald-400" />
            <span className="text-emerald-300 text-sm font-medium">Lancio Pescara 2026</span>
          </div>
          
          <h1 className="text-6xl md:text-7xl lg:text-8xl font-bold text-white mb-6 leading-tight">
            L'affitto.
            <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-blue-400">
              Senza ansia.
            </span>
          </h1>
          
          <p className="text-xl md:text-2xl text-slate-300 mb-12 max-w-3xl mx-auto leading-relaxed">
            Reflow è il sistema operativo per proprietari che vogliono affittare in modo sicuro, 
            selezionando inquilini affidabili e riducendo il rischio.
          </p>
          
          <Button 
            onClick={scrollToForm}
            size="lg" 
            className="bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white px-10 py-7 text-lg rounded-xl shadow-2xl shadow-emerald-500/30 transition-all duration-300 hover:scale-105 hover:shadow-emerald-500/50"
          >
            Diventa Proprietario Fondatore
            <ArrowRight className="ml-2 w-5 h-5" />
          </Button>
          
          <p className="text-slate-400 mt-6 text-sm">
            Accesso limitato ai primi {siteConfig.foundingProperties.total} immobili a Pescara.
          </p>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-10 left-1/2 transform -translate-x-1/2 z-20 animate-bounce">
          <div className="w-6 h-10 border-2 border-white/30 rounded-full flex items-start justify-center p-2">
            <div className="w-1 h-3 bg-white/60 rounded-full"></div>
          </div>
        </div>
      </section>

      {/* Exclusivity Section */}
      <section className="py-24 bg-white relative overflow-hidden">
        <div className="absolute inset-0 bg-grid-slate-100 [mask-image:linear-gradient(0deg,white,rgba(255,255,255,0.6))]"></div>
        
        <div className="max-w-4xl mx-auto px-6 relative z-10">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-50 border border-blue-100 rounded-full mb-6">
              <Lock className="w-4 h-4 text-blue-600" />
              <span className="text-blue-900 text-sm font-semibold">Strategia di Lancio Esclusivo</span>
            </div>
            
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
              Programma Fondatori –
              <br />
              {siteConfig.foundingProperties.total} Immobili
            </h2>
            
            <p className="text-xl text-slate-600 max-w-2xl mx-auto leading-relaxed">
              Stiamo selezionando i primi {siteConfig.foundingProperties.total} proprietari a Pescara per entrare nel circuito iniziale Reflow.
              <br />
              Le attivazioni verranno effettuate progressivamente nelle prossime settimane.
            </p>
          </div>

          <Card className="p-8 md:p-12 bg-gradient-to-br from-slate-900 to-blue-900 border-none shadow-2xl">
            <div className="text-center space-y-4">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-emerald-500/20 rounded-2xl mb-4">
                <Sparkles className="w-10 h-10 text-emerald-400" />
              </div>
              
              <h3 className="text-2xl md:text-3xl font-bold text-white mb-3">
                Entra nel Circuito Fondatori
              </h3>
              
              <p className="text-slate-300 text-lg max-w-xl mx-auto leading-relaxed">
                I proprietari fondatori ricevono priorità nella selezione degli inquilini 
                e supporto dedicato durante tutta la fase di lancio
              </p>
              
              <div className="pt-6">
                <Button 
                  onClick={scrollToForm}
                  size="lg"
                  className="bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white px-10 py-6 text-lg rounded-xl shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105"
                >
                  Candidati Ora
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </div>
            </div>
          </Card>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-24 bg-slate-50">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
              Come Funziona
            </h2>
            <p className="text-xl text-slate-600 max-w-2xl mx-auto">
              Un processo semplice e trasparente
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mb-12">
            {features.map((feature, index) => (
              <Card 
                key={index} 
                className="p-8 bg-white border-slate-200 hover:shadow-xl transition-all duration-300 hover:-translate-y-1 group"
              >
                <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-emerald-500 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                  <feature.icon className="w-7 h-7 text-white" />
                </div>
                
                <div className="flex items-start gap-3 mb-4">
                  <span className="text-3xl font-bold text-emerald-500">{index + 1}</span>
                  <h3 className="text-xl font-bold text-slate-900 leading-tight pt-1">
                    {feature.title}
                  </h3>
                </div>
                
                <p className="text-slate-600">
                  {feature.description}
                </p>
              </Card>
            ))}
          </div>

          <Card className="p-8 md:p-12 bg-gradient-to-r from-slate-900 to-blue-900 border-none text-center">
            <p className="text-2xl md:text-3xl font-bold text-white leading-relaxed">
              Non siamo un semplice portale di annunci.
              <br />
              <span className="text-emerald-400">Siamo il filtro tra te e il rischio.</span>
            </p>
          </Card>
        </div>
      </section>

      {/* Differentiation Section */}
      <section className="py-24 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6">
                Perché Reflow è diverso
              </h2>
              
              <div className="space-y-6 mb-8">
                {differentiators.map((item, index) => (
                  <div key={index} className="flex items-start gap-4 group">
                    <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:bg-emerald-500 transition-colors duration-300">
                      <item.icon className="w-6 h-6 text-emerald-600 group-hover:text-white transition-colors duration-300" />
                    </div>
                    <p className="text-lg text-slate-700 pt-2">
                      {item.text}
                    </p>
                  </div>
                ))}
              </div>

              <Card className="p-6 bg-slate-50 border-slate-200">
                <p className="text-xl font-bold text-slate-900 leading-relaxed">
                  Reflow non è un sito di annunci.
                  <br />
                  <span className="text-emerald-600">È il sistema operativo del tuo affitto.</span>
                </p>
              </Card>
            </div>

            <div className="relative">
              <div className="absolute -inset-4 bg-gradient-to-r from-emerald-500 to-blue-500 rounded-3xl opacity-20 blur-2xl"></div>
              <img 
                src="https://images.unsplash.com/photo-1662454419736-de132ff75638"
                alt="Modern Interior"
                className="relative rounded-2xl shadow-2xl w-full h-auto"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Form Section */}
      <section id="property-form" className="py-24 bg-slate-50">
        <div className="max-w-3xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
              Entra tra i Proprietari Fondatori di Reflow
            </h2>
            <p className="text-lg text-slate-600">
              Le richieste vengono approvate manualmente per garantire qualità.
            </p>
          </div>

          <PropertyForm />
        </div>
      </section>
    </div>
  );
};

export default Home;
